// Core helpers for the Regional Product Movement processor.
// Ported from the standalone Movement Processor tool; behavior is
// intentionally identical, including the parsing quirks these files require.

import { siteToday } from '../../../lib/when';

export const STORAGE_KEYS = {
  src: 'onhand_mp_sourceWarehouse',
  tb: 'onhand_mp_transferredBy',
  mappings: 'onhand_mp_mappings',
};

// These keys used to carry the old product name. They live in the browser, so
// renaming them would have quietly emptied the source warehouse, the
// transferred-by name and every shorthand mapping a person had built up. The
// old key is read once, copied to the new one and removed, so the rename costs
// nobody their settings.
const LEGACY_KEYS = {
  onhand_mp_sourceWarehouse: 'medex_mp_sourceWarehouse',
  onhand_mp_transferredBy:   'medex_mp_transferredBy',
  onhand_mp_mappings:        'medex_mp_mappings',
};

function migrateLegacy(key) {
  const legacy = LEGACY_KEYS[key];
  if (!legacy) return;
  try {
    if (localStorage.getItem(key) !== null) return;
    const carried = localStorage.getItem(legacy);
    if (carried === null) return;
    localStorage.setItem(key, carried);
    localStorage.removeItem(legacy);
  } catch { /* private mode */ }
}

export function lsGet(key, fallback = '') {
  migrateLegacy(key);
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}
export function lsGetJson(key, fallback) {
  migrateLegacy(key);
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}
export function lsSet(key, value) {
  try { localStorage.setItem(key, value); } catch { /* private mode */ }
}

// -- CSV parsing ----------------------------------------------
// Handles quoted fields with embedded commas and doubled "" escapes.
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ',') { row.push(field); field = ''; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += c; i++;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows;
}

// The system exports are UTF-16 LE; detect by BOM, else by zero-byte ratio.
export async function readFileAsText(file) {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xFE) return new TextDecoder('utf-16le').decode(buf);
  if (bytes.length >= 2 && bytes[0] === 0xFE && bytes[1] === 0xFF) return new TextDecoder('utf-16be').decode(buf);
  let zeroCount = 0;
  const sample = Math.min(bytes.length, 200);
  for (let i = 0; i < sample; i++) if (bytes[i] === 0) zeroCount++;
  if (zeroCount > sample * 0.3) return new TextDecoder('utf-16le').decode(buf);
  return new TextDecoder('utf-8').decode(buf);
}

// -- Item numbers ---------------------------------------------
// Strip the Excel formula wrapper the exports use: ="00052" -> 00052
export function cleanItemNumber(raw) {
  let s = String(raw || '').trim();
  const m = s.match(/^="?(.*?)"?$/);
  if (m) s = m[1];
  return s.replace(/^"|"$/g, '').trim();
}

// Ignore dash/space placement so B-25360-3109 matches B-253603109
export function normalizeItem(s) {
  return String(s || '').toLowerCase().replace(/[\s\-_=]/g, '');
}

export function buildProductIndex(products) {
  const index = {};
  for (const p of products) index[normalizeItem(p.itemNumber)] = p;
  return index;
}

export function lookupItem(rawItemNumber, products, productIndex) {
  const clean = cleanItemNumber(rawItemNumber);
  if (!clean) return null;
  const exact = products.find(p => p.itemNumber === clean);
  if (exact) return exact;
  return productIndex[normalizeItem(clean)] || null;
}

// -- Warehouse fuzzy suggestion -------------------------------
const TOKEN_SYNONYMS = {
  specialty: 'speciality',
  speciality: 'specialty',
  inc: 'incarnate',
  st: 'saint',
  saint: 'st',
  wound: 'wound',
  care: 'center',
};

// Token-scored best guess for an account shorthand. Returns null below the
// confidence floor so the user picks from the dropdown instead.
export function suggestWarehouse(shorthand, warehouses) {
  if (!warehouses.length) return null;
  const sh = String(shorthand || '').toLowerCase().replace(/[.,\-]/g, ' ').replace(/\s+/g, ' ').trim();
  const shTokens = sh.split(' ').filter(Boolean);
  if (!shTokens.length) return null;
  const expandedSh = shTokens.flatMap(t => (TOKEN_SYNONYMS[t] ? [t, TOKEN_SYNONYMS[t]] : [t]));

  let best = null;
  let bestScore = 0;
  for (const wh of warehouses) {
    const whNorm = wh.toLowerCase().replace(/^lou\s*-\s*/, '').replace(/[.,\-]/g, ' ').replace(/\s+/g, ' ').trim();
    const whTokens = new Set(whNorm.split(' ').filter(Boolean));

    let score = 0;
    let allTokensMatched = true;
    const seen = new Set();
    for (const t of expandedSh) {
      if (seen.has(t)) continue;
      seen.add(t);
      if (whTokens.has(t)) {
        score += Math.max(t.length, 2) * 2;
      } else {
        let partial = false;
        for (const wt of whTokens) {
          if (wt.startsWith(t) || t.startsWith(wt)) { score += Math.min(t.length, wt.length); partial = true; break; }
        }
        if (!partial && shTokens.includes(t)) allTokensMatched = false;
      }
    }
    if (allTokensMatched) score += 5;
    const tokenDiff = whTokens.size - shTokens.length;
    if (tokenDiff > 3) score -= tokenDiff;

    if (score > bestScore) { bestScore = score; best = wh; }
  }
  return bestScore >= 4 ? best : null;
}

// -- Review rows (workflow A) ---------------------------------
export function buildReviewRows(extractedPages, mappings) {
  const rows = [];
  let order = 0;
  for (const page of extractedPages) {
    const date = page.page_date || '';
    for (const sec of page.sections || []) {
      const sh = sec.shorthand || '';
      const dest = mappings[sh] || '';
      for (const item of sec.items || []) {
        rows.push({
          date,
          shorthand: sh,
          destination: dest,
          itemNumber: item.item_number || '',
          quantity: item.quantity ?? '',
          unit: item.unit || '',
          imageName: page.imageName || '',
          // Which stored page this line came off, so the history can say so
          // after the photograph itself is deleted. Added 8 September 2026.
          pageId: page.pageId || null,
          pageOrder: order++,
        });
      }
    }
  }
  return rows;
}

// Destination group first, original page order within it, so the export keeps
// the order the tech wrote items in.
export function sortReviewRows(rows) {
  return [...rows].sort((a, b) => {
    const ad = a.destination || 'zzz';
    const bd = b.destination || 'zzz';
    if (ad !== bd) return ad.localeCompare(bd);
    return (a.pageOrder || 0) - (b.pageOrder || 0);
  });
}

/*
  What is wrong with one line, in the four words Group G's approved drawing uses.

  **The words changed on 8 September 2026 and the meaning did not.** They read
  "no dest", "item not found" and "unit: CS", which are three abbreviations and a
  colon where the drawing has "No location", "Not in catalog" and "Unit written".
  The quantity that has a unit on it shows the unit inside the quantity cell, so
  the state does not have to carry it.

  **Order matters, and this is the order the file is built in.** A line with no
  location is left out whatever else is true of it, so that is what gets said.
*/
export function rowStatus(row, product) {
  if (!row.destination) return { kind: 'err', label: 'No location' };
  if (!product) return { kind: 'err', label: 'Not in catalog' };
  if (row.unit) return { kind: 'warn', label: 'Unit written' };
  return { kind: 'ok', label: 'OK' };
}

/*
  The lines banded under the location each one goes to, which is Group G's
  approved drawing of Review and the order the file is written in.

  **Its own function so it can be tested**, and because the band's heading is
  not the destination: the drawing splits `LOU-NRMC - OR` into a small grey
  territory `LOU` and a name `NRMC - OR`, which is what makes eight rows across
  four hospitals readable rather than eight rows all starting with the same four
  characters.

  **Lines with nowhere to go are their own band, last.** Rule 9: a line whose
  location nobody has mapped is not a line at a location called nothing, and it
  is the one band somebody has to act on.
*/
export function bandsOf(rows) {
  const bands = [];
  let last;
  for (const r of sortReviewRows(rows)) {
    const dest = r.destination || '';
    if (!bands.length || dest !== last) {
      last = dest;
      /*
        The same two-to-five letters before a dash that `territoryOf` in
        `movementRuns.js` reads to file a run under LOU or TYL. Written as one
        expression in one place so the band above a row and the territory on the
        run it belongs to can never disagree.
      */
      const m = /^([A-Za-z]{2,5})-(.+)$/.exec(dest);
      bands.push({
        key: dest || '__none__',
        destination: dest,
        territory: m ? m[1].toUpperCase() : '',
        name: dest ? (m ? m[2] : dest) : 'No location yet',
        rows: [],
      });
    }
    bands[bands.length - 1].rows.push(r);
  }
  return bands;
}

// -- CSV writers ----------------------------------------------
export function csvField(v) {
  const s = String(v ?? '');
  if (/[",\r\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

// V2 movement import: three fixed preamble lines then one row per movement.
export function buildV2Csv(rows, sourceWarehouse, transferredBy) {
  const lines = [];
  lines.push('V2,,,,,,,,,,');
  lines.push('SourceWarehouse,SourceLocation,SourceBin,DestinationWarehouse,DestinationLocation,DestinationBin,ItemNumber,SerialNumber,Quantity,TransferredBy,Comments');
  lines.push('STRING,OPTIONAL STRING,OPTIONAL STRING,STRING,OPTIONAL STRING,OPTIONAL STRING,STRING,OPTIONAL STRING,NUMBER,STRING,OPTIONAL STRING');

  let written = 0;
  for (const r of sortReviewRows(rows)) {
    if (!r.destination || !r.itemNumber) continue;
    const q = parseInt(r.quantity, 10);
    if (!q || q < 1) continue;
    const commentParts = [];
    if (r.date) commentParts.push(r.date);
    if (r.unit) commentParts.push('Unit: ' + r.unit);
    lines.push([
      csvField(sourceWarehouse), '', '',
      csvField(r.destination), '', '',
      csvField(r.itemNumber), '',
      String(q),
      csvField(transferredBy),
      csvField(commentParts.join(' | ')),
    ].join(','));
    written++;
  }
  return { csv: lines.join('\r\n') + '\r\n', written };
}

/*
  What is not ready, counted by kind.

  **Fault F's approved fix, 8 September 2026.** Until today three things counted
  as a problem, and **a unit written beside the quantity was not one of them**.
  So `1 CS` of DVT large cuffs, which is twenty of them, went into the file as a
  plain 1 with the word "CS" tucked into a free-text comment the receiving
  system may not read at all, and **the "there are N issues" box never
  appeared**. The only warning was an amber mark on a screen nobody is looking
  at when the file is made.

  It is a problem now, and it is counted separately from the other three,
  because it is a different KIND of problem with a different answer:

  - no location, no item number, no usable quantity: the file physically cannot
    carry the line, so it is left out whatever anybody chooses
  - a unit written: the line goes in, as a plain number. That is a compromise
    somebody has to accept on purpose rather than find out about months later

  Counted by kind rather than as a list of sentences, because the approved
  dialog shows "1 line has no location" and not a paragraph per row.
*/
export function notReady(rows, inCatalog) {
  const kinds = { noLocation: 0, noItemNumber: 0, notInCatalog: 0, badQuantity: 0, unitWritten: 0 };
  let cannotGoIn = 0;
  for (const r of rows) {
    if (!r.destination) { kinds.noLocation += 1; cannotGoIn += 1; continue; }
    if (!r.itemNumber) { kinds.noItemNumber += 1; cannotGoIn += 1; continue; }
    const q = parseInt(r.quantity, 10);
    if (!q || q < 1) { kinds.badQuantity += 1; cannotGoIn += 1; continue; }
    /*
      **An item number the catalog has never heard of.** Added 8 September 2026
      after photographing the dialog beside the approved drawing and finding it
      counted two problems where the drawing counts three.

      It is a real problem and a DIFFERENT one from the three above: the file
      can carry it perfectly well, and the system reading the file is the one
      that will reject it. So it is counted, and it still goes in, like a unit.
      Only asked when the caller has a catalog to ask; a screen that does not
      know cannot pretend to.
    */
    if (inCatalog && !inCatalog(r.itemNumber)) { kinds.notInCatalog += 1; continue; }
    if (r.unit) kinds.unitWritten += 1;
  }
  const total = kinds.noLocation + kinds.noItemNumber + kinds.notInCatalog
              + kinds.badQuantity + kinds.unitWritten;
  /*
    What the file will actually hold if it is made as it stands. **Only the
    three the file physically cannot carry come off.** A unit line and an
    unknown item number both go in: those are compromises to accept on purpose,
    not lines to drop.
  */
  return { ...kinds, total, willHold: rows.length - cannotGoIn, of: rows.length };
}

/*
  What happened to one row, in the same three words the history shows.

  Added 8 September 2026 for "Build the Ordering area", and **it deliberately
  repeats `buildV2Csv`'s own skip rules rather than guessing at them.** The
  history and the file are two accounts of the same run, and the one thing that
  must never happen is the two disagreeing: a line the file quietly dropped
  showing as "in the file" months later is worse than no history at all.

  So the rules here are the file's rules, in the file's order:

    no destination      the file skips it
    no item number      the file skips it
    quantity under 1    the file skips it, blanks included
    a unit written      it GOES IN, as a plain number, which is fault F and the
                        reason this word exists at all
*/
export function outcomeFor(row) {
  if (!row.destination) return { outcome: 'left_out', why: 'No location' };
  if (!row.itemNumber)  return { outcome: 'left_out', why: 'No item number' };
  const q = parseInt(row.quantity, 10);
  if (!q || q < 1) return { outcome: 'left_out', why: 'No usable quantity' };
  if (row.unit) return { outcome: 'in_file_as_1', why: 'Written as ' + row.quantity + ' ' + row.unit };
  return { outcome: 'in_file', why: null };
}

/*
  **`validateReviewRows` and `unitWarnings` were removed on 8 September 2026**,
  replaced by `notReady` above.

  They were two functions answering one question in two shapes, which is exactly
  why fault F could exist: the first counted three problems as a list of
  sentences and did not count a unit at all, the second counted units on their
  own, and the screen asked about the same file twice in two browser boxes. One
  function counts all four now, by kind, which is what the approved dialog
  shows.

  Written down rather than deleted quietly, because a search for either name in
  six months should find out where they went.
*/

// Reorder import: 28 columns, only item / ordered / received are populated.
export const REORDER_IMPORT_HEADERS = [
  'TONumber', 'NewTONumber', 'PONumber', 'Status', 'SourceWarehouse', 'DestinationWarehouse',
  'Notes', 'CreatedBy', 'CreatedDate', 'TOModifiedBy', 'TOModifiedDate', 'DateReleased',
  'DateSent', 'DateClosed', 'ItemNumber', 'SerialNumber', 'Location', 'Bin', 'Description',
  'Vendor', 'QuantityOrdered', 'QuantityReceived', 'ModifiedBy', 'ModifiedDate', 'ReceivedBy',
  'DateReceived', 'ConvertToPO', 'POQuantity',
];

export function buildReorderCsv(reorderRows) {
  const itemIdx = REORDER_IMPORT_HEADERS.indexOf('ItemNumber');
  const qtyOrderedIdx = REORDER_IMPORT_HEADERS.indexOf('QuantityOrdered');
  const qtyReceivedIdx = REORDER_IMPORT_HEADERS.indexOf('QuantityReceived');
  const lines = [REORDER_IMPORT_HEADERS.join(',')];
  for (const r of reorderRows) {
    const cols = new Array(REORDER_IMPORT_HEADERS.length).fill('');
    cols[itemIdx] = csvField(r.itemNumber);
    cols[qtyOrderedIdx] = String(parseInt(r.qty, 10) || 0);
    cols[qtyReceivedIdx] = '0';
    lines.push(cols.join(','));
  }
  return lines.join('\r\n') + '\r\n';
}

// -- Weekly reorder consolidation (workflow B) ----------------
export function parseReport(text) {
  const allRows = parseCsv(text);
  let headerIdx = -1;
  for (let i = 0; i < allRows.length; i++) {
    const r = allRows[i].map(c => (c || '').trim());
    if (r.includes('ItemNumber') && r.includes('QOHChange')) { headerIdx = i; break; }
  }
  if (headerIdx === -1) throw new Error('Could not find the data header (ItemNumber / QOHChange columns) in the report.');

  const headers = allRows[headerIdx].map(h => (h || '').trim());
  const itemIdx = headers.indexOf('ItemNumber');
  const qohIdx = headers.indexOf('QOHChange');
  const descIdx = headers.indexOf('Description');
  const vendorIdx = headers.indexOf('VendorName');

  const map = {};
  const skipped = [];
  let dataRowCount = 0;

  for (let i = headerIdx + 1; i < allRows.length; i++) {
    const r = allRows[i];
    if (!r || r.every(c => !(c || '').trim())) continue;
    dataRowCount++;
    const cleanItem = cleanItemNumber((r[itemIdx] || '').trim());
    const rawQoh = (r[qohIdx] || '').trim();

    if (!cleanItem) { skipped.push({ reason: 'blank item number', row: i + 1 }); continue; }
    const qNum = parseFloat(rawQoh);
    if (isNaN(qNum)) { skipped.push({ reason: `unparseable quantity "${rawQoh}"`, item: cleanItem, row: i + 1 }); continue; }

    if (!map[cleanItem]) {
      map[cleanItem] = {
        itemNumber: cleanItem,
        description: (r[descIdx] || '').trim(),
        vendor: vendorIdx >= 0 ? (r[vendorIdx] || '').trim() : '',
        values: [],
        qty: 0,
        hadPositive: false,
      };
    }
    map[cleanItem].values.push(qNum);
    map[cleanItem].qty += Math.abs(qNum);
    if (qNum > 0) map[cleanItem].hadPositive = true;
  }

  const rows = Object.values(map).map(x => ({ ...x, qty: Math.round(x.qty) }));
  rows.sort((a, b) => a.itemNumber.localeCompare(b.itemNumber, undefined, { numeric: true }));
  return { rows, skipped, dataRowCount };
}

export function downloadCsv(csv, filename) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// The site's day, not Greenwich's: a file saved on a Texas evening was named
// for tomorrow (the audit after the rebrand, issue 23, 18 September 2026).
export function todayStamp() {
  return siteToday();
}
